import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef
} from 'react';
import {
  Drawer,
  Tooltip,
  Button,
  ButtonType
} from 'syngenta-digital-cropwise-react-ui-kit';
import { Dropdown } from 'antd';
import usePopulateMapData from 'hooks/usePopulateMapData';
import { FETCH_TYPES } from 'helpers/constants';
import { getTotalAreaNumber } from 'helpers/getTotalAreaAcres';
import formatNumber from 'helpers/formatNumber';
import getSeasons from 'screens/Yield/components/YieldActions/helpers/getSeasons';
import SearchInputLandingProperties from 'screens/Property/PropertiesLanding/components/SearchInputLandingProperties';
import { useStore } from 'components/Store';
import SortIcon from 'assets/sort.svg';
import InfoIcon from 'assets/info.svg';
import CropIconMapping from 'components/CropIconMapping';
import useCropData from 'hooks/useCropData';
import { getSelectedCropZones } from 'screens/Reports/helpers/helperFunctions';
import { SEARCH_TEXT_VALUE_FFT } from 'reducers/reducer';
import { LAST_COORDINATES_STORED } from 'screens/Property/helpers/constants';
import SelectionMap from 'components/SelectPropertiesDrawerV2/components/SelectionMap';
import { calculateCentroidByEachProperties } from 'screens/Property/helpers/mapApiHelpers';
import { getStringParsed } from 'utilities/helpers';
import groupByCrop from 'screens/Property/helpers/groupByCrops';
import { usePreferences, PreferenceKey } from 'hooks/usePreferences';
import focusInput from 'screens/Applications&WorkOrders/StepsLayout/Helpers/focusInput';
import { useAuth } from 'utilities/base-auth';
import showConfirmModal from 'components/ConfirmModal';
import LayoutStrings from 'screens/Applications&WorkOrders/StepsLayout/Constants/layoutStrings';
import ReportProblemIcon from 'assets/report_problem.svg?react';
import SpinnerIcon from 'assets/spinner_modal.svg';
import CropSeasonDisplay from 'components/CropSeasonDisplay';
import FieldZoneIcon from 'components/FieldZoneIcon';
import { bbox, featureCollection } from '@turf/turf';
import matchFeaturesAndSelectedCropzones from 'screens/YieldV2/Loads/components/LoadsFilterDrawer/components/SelectPropertiesFilter/helpers/matchFeaturesAndSelectedCropzones';
import PropertySelectorV2 from 'components/SelectPropertiesDrawerV2/components/PropertySelectorV2';
import searchByTextWithinFFT from 'screens/YieldV2/Loads/components/LoadsFilterDrawer/components/SelectPropertiesFilter/helpers/searchByTextWithinFFT';
import searchByTextWithinFFTGranular from 'components/SelectPropertiesDrawerV2/helpers/searchByTextWithinFFTGranular';
import { filterPropertiesCropZones } from 'screens/YieldV2/Loads/components/LoadsFilterDrawer/components/SelectPropertiesFilter/helpers/filterCropZonesHelpers';
import './index.css';

const GROUP_BY_FARM = 'farm';
const GROUP_BY_CROP = 'crop';

interface SelectionConfirmPayload {
  cropZoneIds: string[];
  totalArea: number;
  cropZones: any[];
  sourceType: string;
}

export interface SelectionAdapter {
  initialCropZones: string[] | Array<{ id: string; [key: string]: any }>;
  existingCropZones: any[];
  onConfirm: (payload: SelectionConfirmPayload) => void;
  /**
   * Crop zone ids that must remain visible in the tree even after being
   * deselected and saved — e.g. archived crop zones already associated
   * with the record when it was loaded for editing. Sourced from state
   * that survives the host screen's own remounts (the drawer's in-memory
   * history alone doesn't: a host that unmounts SelectPropertiesDrawerV2,
   * e.g. a multi-step wizard swapping steps, loses it otherwise).
   */
  alwaysVisibleCropZoneIds?: string[];
}

interface SelectPropertiesDrawerV2Props {
  open: boolean;
  onClose: () => void;
  selectionAdapter: SelectionAdapter;
  seasonIds?: string[];
  /**
   * Crop id that scopes the drawer to a single crop. When provided:
   *  - filters the tree to crop zones in that crop
   *  - filters the map to features whose crop zones are in that crop
   *  - renders the crop name + "Only properties with the selected crop are
   *    listed" header
   * Undefined/empty means multi-crop mode — Apps/WO and the Loads filter
   * (which don't set this) keep their current behavior.
   */
  restrictToCropId?: string;
}

// ---------------------------------------------------------------------------
// Module-level helpers (pure, no component state)
// ---------------------------------------------------------------------------
// App-state features hold `cropzones` as an array; Mapbox returns rendered
// features with it serialized to a string.
const parseCropZoneFeatures = (feature: any): Array<{ id: string }> | null => {
  const cropzones = feature?.properties?.cropzones;
  if (Array.isArray(cropzones)) return cropzones;
  try {
    return JSON.parse(cropzones);
  } catch {
    return null;
  }
};

const toggleCropZonesSelection = (
  prevSelection: string[],
  targets: Array<{ id: string }>
): string[] => {
  const ids = targets.map(({ id }: { id: string }) => id);
  const anyNotSelected = ids.some((id: string) => !prevSelection.includes(id));
  if (anyNotSelected) return [...new Set([...prevSelection, ...ids])];
  return prevSelection.filter((id: string) => !ids.includes(id));
};

// Module-level so the .forEach chain isn't nested inside the component +
// useMemo — keeps function-nesting depth within sonar's 4-level limit.
const collectAllowedIds = (
  properties: any[]
): { allowedCropZoneIds: Set<string>; allowedFieldIds: Set<string> } => {
  const allowedCropZoneIds = new Set<string>();
  const allowedFieldIds = new Set<string>();
  properties.forEach((farm: any) => {
    (farm?.fields ?? []).forEach((field: any) => {
      if (field?.id) allowedFieldIds.add(field.id);
      (field?.cropzones ?? []).forEach((cz: any) => {
        if (cz?.id) allowedCropZoneIds.add(cz.id);
      });
    });
  });
  return { allowedCropZoneIds, allowedFieldIds };
};

// ---------------------------------------------------------------------------
// SelectPropertiesDrawerV2
// ---------------------------------------------------------------------------
const SelectPropertiesDrawerV2: React.FC<SelectPropertiesDrawerV2Props> = ({
  open,
  onClose,
  selectionAdapter,
  seasonIds = [],
  restrictToCropId
}) => {
  const {
    state: { cropSeasons, searchTextValueFFT, isGlobalCropSeasonLoading },
    dispatch
  } = useStore();
  const allSeasonIds = useMemo(() => getSeasons() ?? [], []);
  const selectedSeasonIds = useMemo(
    () => (seasonIds?.length ? seasonIds : allSeasonIds),
    [seasonIds, allSeasonIds]
  );
  const [properties, setProperties] = useState<any[]>([]);
  // "FFT filter has run" vs "list is legitimately empty" — see restrictedIds.
  const [fftListReady, setFftListReady] = useState(false);
  const [groupBy, setGroupBy] = useState(GROUP_BY_FARM);
  const { findPreferenceByKey, upsertPreference } = usePreferences();
  const preferenceLoaded = useRef(false);
  // Every crop zone id ever seen as "initially selected" across opens of
  // this drawer instance (not reset when the drawer closes). An archived
  // crop zone only survives filterPropertiesCropZones while its id is in
  // the snapshot below — without this history, deselecting an archived
  // crop zone and saving removes it from the adapter's selection, so the
  // next open would compute a snapshot without it and it (and its parent
  // field/farm) would vanish from the tree with no way to bring it back.
  const everSelectedCropZoneIdsRef = useRef<Set<string>>(new Set());
  const [
    initiallySelectedCropZoneIdsSnapshot,
    setInitiallySelectedCropZoneIdsSnapshot
  ] = useState<string[]>([]);
  const initializeCropZonesChecked = () => {
    const fieldValue = selectionAdapter.initialCropZones;
    if (!fieldValue || !Array.isArray(fieldValue)) return [];
    if (
      fieldValue.length > 0 &&
      typeof fieldValue[0] === 'object' &&
      (fieldValue[0] as { id: string }).id
    ) {
      return (fieldValue as Array<{ id: string }>).map(item => item.id);
    }
    return fieldValue as string[];
  };

  const initialCropZones = initializeCropZonesChecked();
  const [cropZonesChecked, setCropZonesChecked] = useState(initialCropZones);
  const cropZonesCheckedRef = useRef(cropZonesChecked);
  useEffect(() => {
    cropZonesCheckedRef.current = cropZonesChecked;
  }, [cropZonesChecked]);
  const [scrollToField, setScrollToField] = useState<{
    farmId: string;
    fieldId: string;
    cropId?: string;
  } | null>(null);
  const savedCropZonesRef = useRef(initialCropZones);
  const prevCropZonesRef = useRef<string[]>(initialCropZones);
  const skipNextFitBoundsRef = useRef(false);
  const didInitialFitRef = useRef(false);
  const searchWrapperRef = useRef(null);
  const { user } = useAuth();

  const [showSelectAllOverlay, setShowSelectAllOverlay] = useState(false);
  const selectAllRafRef = useRef<number | null>(null);
  const selectAllTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const searchLoadingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const activeSearchTerm = searchTextValueFFT.valueInput ?? '';

  // Merge with existing selection. For non-search `idsToSelect` is the full
  // list so merge == replace; for search it adds visible IDs without dropping
  // hidden ones. Extracted from the deferred scheduler below so the
  // raf/timeout chain stays under Sonar's 4-level nesting cap.
  const commitSelectAll = useCallback((idsToSelect: string[]) => {
    setCropZonesChecked((prev: string[]) =>
      Array.from(new Set([...prev, ...idsToSelect]))
    );
    setShowSelectAllOverlay(false);
  }, []);

  const handleSelectAll = useCallback(
    (idsToSelect: string[]) => {
      setShowSelectAllOverlay(true);
      selectAllRafRef.current = requestAnimationFrame(() => {
        selectAllTimeoutRef.current = setTimeout(
          () => commitSelectAll(idsToSelect),
          0
        );
      });
    },
    [commitSelectAll]
  );

  const [manualCoords, setManualCoords] = useState(
    getStringParsed(localStorage.getItem(LAST_COORDINATES_STORED))
  );
  const [fitBoundsTarget, setFitBoundsTarget] = useState<
    [[number, number], [number, number]] | null
  >(null);

  const { crops } = useCropData(restrictToCropId);

  const {
    loading,
    dataSorted: propertyLandingPageData,
    fieldsAndAreasGeoJSONCollection,
    zoom,
    setZoom,
    fieldsLoading,
    reloadData
  } = usePopulateMapData({
    fetchType: FETCH_TYPES.cropSeasonsMap,
    cropSeasonsId: selectedSeasonIds,
    isCacheOn: true,
    includeArchived: true
  });

  const propertiesRef = useRef<any[]>(
    propertyLandingPageData?.properties ?? []
  );
  useEffect(() => {
    propertiesRef.current = propertyLandingPageData?.properties ?? [];
  }, [propertyLandingPageData?.properties]);

  const filteredPropertiesRef = useRef<any[]>([]);
  useEffect(() => {
    filteredPropertiesRef.current = properties;
  }, [properties]);

  const geoJSONCentroid = useMemo(
    () =>
      JSON.stringify(
        calculateCentroidByEachProperties(
          fieldsAndAreasGeoJSONCollection,
          properties
        )
      ),
    [fieldsAndAreasGeoJSONCollection, properties]
  );

  // Drop map features whose target id (cropzone or field) is not in the
  // tree's filtered set so the map can't render shapes the user can't act on,
  // or worse silently toggle hidden cropzones into the selection. The tree
  // drops fields with no crop zone in the selected seasons (or crop), and
  // archived fields unless initially selected; the map must mirror that.
  //
  // The collection mixes feature shapes: cropzone-level features carry the
  // cropzone id on properties.id (matched by matchFeaturesAndSelectedCropzones)
  // while field-level features carry the field id on properties.id (matched
  // by handleClickedFeature). We allow both and drop features without an id.
  //
  // Also rewrite each kept field feature's properties.cropzones JSON to only
  // include allowed cropzones — handleClickedFeature parses that JSON to
  // decide which cropzones a field click toggles, and a mixed-crop field
  // (e.g. corn + wheat with cropId=corn) would otherwise still toggle the
  // non-allowed wheat ids on click, even though they're not in the tree.
  //
  // Hoisted so both mapGeoJSON and handleClickedFeature can intersect against
  // the same allow-list. Gated on "filter has run", NOT on list length: a
  // legitimately empty list must still hide everything, while null (before
  // the first filter run on settled data) passes the map through unchanged.
  const restrictedIds = useMemo(
    () => (fftListReady ? collectAllowedIds(properties) : null),
    [fftListReady, properties]
  );

  // Ref so the click callback can read the latest allow-list without
  // forcing handleClickedFeature to re-memoize on every properties change.
  const restrictedIdsRef = useRef(restrictedIds);
  useEffect(() => {
    restrictedIdsRef.current = restrictedIds;
  }, [restrictedIds]);

  const mapGeoJSON = useMemo(() => {
    if (!restrictedIds) return fieldsAndAreasGeoJSONCollection;
    const { allowedCropZoneIds, allowedFieldIds } = restrictedIds;
    return {
      ...fieldsAndAreasGeoJSONCollection,
      features: (fieldsAndAreasGeoJSONCollection?.features ?? [])
        .filter((f: any) => {
          const id = f?.properties?.id;
          return (
            !!id && (allowedFieldIds.has(id) || allowedCropZoneIds.has(id))
          );
        })
        .map((f: any) => {
          if (f?.properties?.$landType !== 'field') return f;
          const czs = parseCropZoneFeatures(f);
          if (!czs) return f;
          const filteredCzs = czs.filter(({ id }: { id: string }) =>
            allowedCropZoneIds.has(id)
          );
          // Same length → JSON didn't need pruning, keep the original
          // reference so downstream identity-based memos don't churn.
          if (filteredCzs.length === czs.length) return f;
          return {
            ...f,
            properties: {
              ...f.properties,
              cropzones: Array.isArray(f.properties.cropzones)
                ? filteredCzs
                : JSON.stringify(filteredCzs)
            }
          };
        })
    };
  }, [fieldsAndAreasGeoJSONCollection, restrictedIds]);

  const mapCoordinates = useMemo(
    () => manualCoords || JSON.parse(geoJSONCentroid)?.geometry?.coordinates,
    [geoJSONCentroid, manualCoords]
  );

  useEffect(() => {
    if (open && selectedSeasonIds?.length) {
      reloadData({ seasonIds: selectedSeasonIds });
    }
  }, [open, selectedSeasonIds, reloadData]);

  useEffect(() => {
    if (open) {
      const initialIds = initializeCropZonesChecked();
      // Re-sync the local checked state from the adapter on every open.
      // Without this the drawer keeps the first-mount selection, so any
      // change the parent makes while the drawer is closed (e.g. deleting a
      // row from the table) is invisible the next time it opens.
      // Also update cropZonesCheckedRef synchronously so the fit-bounds
      // effect (which fires later in the same commit and reads via the ref)
      // sees the resynced selection — otherwise the map fits to the stale
      // pre-resync ids and never corrects, since didInitialFitRef guards it.
      cropZonesCheckedRef.current = initialIds;
      setCropZonesChecked(initialIds);
      savedCropZonesRef.current = initialIds;
      prevCropZonesRef.current = initialIds;
      setFitBoundsTarget(null);
      didInitialFitRef.current = false;
    } else {
      // Reset map-interaction state so stale values don't bleed into the next
      // open cycle (e.g. skipNextFitBoundsRef left true after a map deselect,
      // or scrollToField left non-null and re-triggering a scroll on reopen).
      skipNextFitBoundsRef.current = false;
      setScrollToField(null);
      // The reload a reopen triggers only reports loading on the next render,
      // so a ready flag left over from this open would let the one-shot fit
      // measure the previous open's list and never refit to the new data.
      setFftListReady(false);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // One-shot fit on open. Reads mapGeoJSON and waits for fftListReady so the
  // fit never measures features the map won't render (archived/restricted).
  useEffect(() => {
    if (!open || loading || !fftListReady || didInitialFitRef.current) return;
    const features = (mapGeoJSON?.features ?? []).filter(
      (f: any) => f?.geometry?.coordinates
    );
    if (features.length === 0) return;
    didInitialFitRef.current = true;
    // Read from the ref, not state. State may not have flushed yet on the
    // same-commit re-sync from the open useEffect above, but the ref is
    // updated synchronously there.
    const currentChecked = cropZonesCheckedRef.current;
    const targetFeatures =
      currentChecked.length > 0
        ? matchFeaturesAndSelectedCropzones(mapGeoJSON, currentChecked)
        : features;
    const fitFeatures = targetFeatures.length > 0 ? targetFeatures : features;
    const [minX, minY, maxX, maxY] = bbox(featureCollection(fitFeatures));
    setFitBoundsTarget([
      [minX, minY],
      [maxX, maxY]
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, loading, fftListReady, mapGeoJSON]);

  useEffect(() => {
    if (open) {
      const initiallySelectedIds = initializeCropZonesChecked();
      initiallySelectedIds.forEach(id =>
        everSelectedCropZoneIdsRef.current.add(id)
      );
      (selectionAdapter.alwaysVisibleCropZoneIds ?? []).forEach(id =>
        everSelectedCropZoneIdsRef.current.add(id)
      );
      const mergedIds = Array.from(everSelectedCropZoneIdsRef.current);
      setInitiallySelectedCropZoneIdsSnapshot(mergedIds);
    } else {
      setInitiallySelectedCropZoneIdsSnapshot([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const filteredProperties = filterPropertiesCropZones(
      propertyLandingPageData.properties,
      selectedSeasonIds,
      restrictToCropId,
      initiallySelectedCropZoneIdsSnapshot
    );
    setProperties(filteredProperties);
    // Arm only with settled data, and disarm on every reload: a list filtered
    // from the previous load would otherwise hide the freshly loaded features
    // (and the one-shot fit would measure that partial set).
    setFftListReady(!loading);
  }, [
    initiallySelectedCropZoneIdsSnapshot,
    open,
    propertyLandingPageData,
    selectedSeasonIds,
    restrictToCropId,
    loading
  ]);

  // Zoom to selected features when selection grows via FFT; preserve view on
  // deselect and when selection comes from the map itself.
  //
  // When search is active, zoom to only the *newly added* IDs so that
  // pre-existing selections outside the current search scope don't pull the
  // map away from the user's current context.
  // When search is inactive, zoom to the full selection (normal behaviour).
  useEffect(() => {
    if (loading) return;
    const prevIds = prevCropZonesRef.current;
    const currLength = cropZonesChecked.length;
    prevCropZonesRef.current = cropZonesChecked;

    if (currLength > prevIds.length && currLength > 0) {
      if (skipNextFitBoundsRef.current) {
        skipNextFitBoundsRef.current = false;
        return;
      }
      const targetIds = activeSearchTerm
        ? (() => {
            const prevSet = new Set(prevIds);
            return cropZonesChecked.filter(id => !prevSet.has(id));
          })()
        : cropZonesChecked;
      const selectedFeatures = matchFeaturesAndSelectedCropzones(
        mapGeoJSON,
        targetIds
      );
      if (selectedFeatures.length > 0) {
        const [minX, minY, maxX, maxY] = bbox(
          featureCollection(selectedFeatures)
        );
        setFitBoundsTarget([
          [minX, minY],
          [maxX, maxY]
        ]);
      }
    }
    // Deselect → do not update fitBoundsTarget so the map stays put.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cropZonesChecked.length, loading]);

  useEffect(() => {
    if (open) focusInput(searchWrapperRef, 'input');
  }, [open]);

  useEffect(() => {
    if (!open) {
      if (selectAllRafRef.current !== null)
        cancelAnimationFrame(selectAllRafRef.current);
      if (selectAllTimeoutRef.current !== null)
        clearTimeout(selectAllTimeoutRef.current);
      setShowSelectAllOverlay(false);
    }
    return () => {
      if (selectAllRafRef.current !== null)
        cancelAnimationFrame(selectAllRafRef.current);
      if (selectAllTimeoutRef.current !== null)
        clearTimeout(selectAllTimeoutRef.current);
    };
  }, [open]);

  useEffect(() => {
    preferenceLoaded.current = false;
  }, [user?.id]);

  useEffect(() => {
    if (open && user?.id && !preferenceLoaded.current) {
      findPreferenceByKey(PreferenceKey.PROPERTY_GROUP_BY, { userId: user.id })
        .then(pref => {
          if (pref?.preferenceValue?.groupBy) {
            setGroupBy(pref.preferenceValue.groupBy.toLowerCase());
          }
        })
        .catch(() => {})
        .finally(() => {
          preferenceLoaded.current = true;
        });
    }
  }, [open, findPreferenceByKey, user?.id]);

  const handleGroupByChange = useCallback(
    (newGroupBy: string) => {
      setGroupBy(newGroupBy);
      if (!user?.id) return;
      upsertPreference({
        preferenceKey: PreferenceKey.PROPERTY_GROUP_BY,
        preferenceValue: { groupBy: newGroupBy.toUpperCase() },
        userId: user.id
      }).catch(() => {});
    },
    [upsertPreference, user?.id]
  );

  const renderSortCheckmark = (isActive: boolean) => (
    <svg
      width="20"
      height="19"
      viewBox="0 0 25 24"
      fill="none"
      className="shrink-0 mr-1 ml-2"
    >
      <path
        d="M8.8273 15.8769L4.6573 11.7069L3.2373 13.1169L8.8273 18.7069L20.8273 6.70687L19.4173 5.29688L8.8273 15.8769Z"
        fill={isActive ? '#0092E4' : 'transparent'}
      />
    </svg>
  );

  const sortMenuItems = [
    {
      key: GROUP_BY_CROP,
      className: groupBy === GROUP_BY_CROP ? 'selected-item' : '',
      label: (
        <div
          className={`flex items-center w-full h-full ${
            groupBy === GROUP_BY_CROP ? 'font-semibold' : ''
          }`}
        >
          {renderSortCheckmark(groupBy === GROUP_BY_CROP)}
          <span className="px-2 py-1">Group by crop</span>
        </div>
      )
    },
    {
      key: GROUP_BY_FARM,
      className: groupBy === GROUP_BY_FARM ? 'selected-item' : '',
      label: (
        <div
          className={`flex items-center w-full h-full ${
            groupBy === GROUP_BY_FARM ? 'font-semibold' : ''
          }`}
        >
          {renderSortCheckmark(groupBy === GROUP_BY_FARM)}
          <span className="px-2 py-1">Group by farm</span>
        </div>
      )
    }
  ];

  const sortMenuProps = {
    items: sortMenuItems,
    onClick: ({ key }: { key: string }) => handleGroupByChange(key)
  };

  const totalArea = useMemo(
    () =>
      getTotalAreaNumber(
        matchFeaturesAndSelectedCropzones(mapGeoJSON, cropZonesChecked)
      ),
    [cropZonesChecked, mapGeoJSON]
  );

  const filteredProperties = useMemo(
    () =>
      activeSearchTerm
        ? searchByTextWithinFFTGranular(properties, activeSearchTerm)
        : searchByTextWithinFFT(properties, activeSearchTerm),
    [properties, activeSearchTerm]
  );

  const visibleFieldIds = useMemo<Set<string> | null>(() => {
    if (!activeSearchTerm) return null;
    const ids = new Set<string>();
    filteredProperties.forEach((farm: any) => {
      farm.fields?.forEach((field: any) => {
        ids.add(field.id);
      });
    });
    return ids;
  }, [filteredProperties, activeSearchTerm]);

  const handleClearSearch = useCallback(() => {
    dispatch({ type: SEARCH_TEXT_VALUE_FFT, payload: { valueInput: '' } });
  }, [dispatch]);

  useEffect(() => {
    if (searchLoadingTimerRef.current)
      clearTimeout(searchLoadingTimerRef.current);
    if (!activeSearchTerm) {
      setIsSearchLoading(false);
      return undefined;
    }
    searchLoadingTimerRef.current = setTimeout(() => {
      setIsSearchLoading(true);
    }, 2000);
    return () => {
      if (searchLoadingTimerRef.current)
        clearTimeout(searchLoadingTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSearchTerm]);

  useEffect(() => {
    if (searchLoadingTimerRef.current) {
      clearTimeout(searchLoadingTimerRef.current);
      searchLoadingTimerRef.current = null;
    }
    setIsSearchLoading(false);
  }, [filteredProperties]);

  // Farm totals ("X of Y fields") for search and crop-group mode — same gate
  // as fieldCzCountMap below.
  const searchCountMap = useMemo<Record<string, number>>(() => {
    if (!activeSearchTerm && groupBy !== GROUP_BY_CROP) return {};
    const map: Record<string, number> = {};
    properties.forEach((farm: any) => {
      map[farm.id] = farm.fields?.length ?? 0;
    });
    return map;
  }, [properties, activeSearchTerm, groupBy]);

  const totalFieldCzCountMap = useMemo<Record<string, number>>(() => {
    const map: Record<string, number> = {};
    properties.forEach((farm: any) => {
      farm.fields?.forEach((field: any) => {
        map[field.id] = field.cropzones?.length ?? 0;
      });
    });
    return map;
  }, [properties]);

  const fieldCzCountMap = useMemo<Record<string, number>>(() => {
    if (!activeSearchTerm && groupBy !== GROUP_BY_CROP) return {};
    return totalFieldCzCountMap;
  }, [activeSearchTerm, groupBy, totalFieldCzCountMap]);

  const groupedProperties = useMemo(() => {
    if (groupBy === GROUP_BY_CROP && filteredProperties?.length > 0) {
      return groupByCrop(filteredProperties);
    }
    return filteredProperties;
  }, [groupBy, filteredProperties]);

  const handleClickedFeature = useCallback(
    (clicked: any) => {
      if (clicked?.properties?.$landType !== 'field') return;
      const fieldId: string = clicked?.properties?.id;
      // Skip archived fields not in the drawer list (not initially selected).
      // Only archived fields need the guard — non-archived always pass through.
      const fieldInData = (propertiesRef.current ?? [])
        .flatMap((farm: any) => farm.fields ?? [])
        .find((f: any) => f.id === fieldId);
      if (fieldInData?.archived) {
        const isInDrawer = (
          filteredPropertiesRef.current ?? []
        ).some((farm: any) =>
          (farm.fields ?? []).some((f: any) => f.id === fieldId)
        );
        if (!isInDrawer) return;
      }
      const parsedTargets = parseCropZoneFeatures(clicked);
      if (!parsedTargets || parsedTargets.length === 0) return;

      // In single-crop mode, intersect against the allow-list directly from
      // app state. The mapGeoJSON memo already prunes the cropzones JSON on
      // field features, but Mapbox's queryRenderedFeatures can return
      // features from its internal tile cache with the pre-prune properties
      // (source.setData doesn't always invalidate already-rendered tiles
      // until the viewport changes). Filtering here makes the click target
      // independent of Mapbox's cache state.
      const restricted = restrictedIdsRef.current;
      const targets = restricted
        ? parsedTargets.filter(({ id }: { id: string }) =>
            restricted.allowedCropZoneIds.has(id)
          )
        : parsedTargets;
      if (targets.length === 0) return;

      const ids = targets.map(({ id }: { id: string }) => id);
      const isSelecting = ids.some(
        id => !cropZonesCheckedRef.current.includes(id)
      );
      // Only suppress fitBounds when the selection is growing. A deselect
      // never triggers fitBounds (effect guards on currLength > prevLength),
      // so setting the flag on deselect would leave it dirty and incorrectly
      // suppress the next FFT-driven fitBounds.
      if (isSelecting) skipNextFitBoundsRef.current = true;
      setCropZonesChecked(prev => toggleCropZonesSelection(prev, targets));

      if (!isSelecting || !fieldId) return;
      const farm = (propertiesRef.current ?? []).find((prop: any) =>
        (prop.fields ?? []).some((f: any) => f.id === fieldId)
      );
      if (farm) {
        const clickedField = (farm.fields ?? []).find(
          (f: any) => f.id === fieldId
        );
        const cropId = (clickedField?.cropzones ?? []).find((cz: any) =>
          ids.includes(cz.id)
        )?.crop?.id;
        setScrollToField({ farmId: farm.id, fieldId, cropId });
      }
    },
    [setCropZonesChecked, setScrollToField] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const onSelectFromMap = useCallback(
    (event: any) => {
      (event?.features ?? []).forEach(handleClickedFeature);
    },
    [handleClickedFeature]
  );

  const activeCropSeasonNames = useMemo(
    () =>
      cropSeasons
        .filter((cs: any) => selectedSeasonIds.includes(cs.id))
        .map(({ name }: { name: string }) => name)
        .join(' | '),
    [cropSeasons, selectedSeasonIds]
  );

  const handleDiscardPropertySelection = () => {
    setCropZonesChecked(savedCropZonesRef.current);
    handleClearSearch();
    onClose();
  };

  const hasUnsavedChanges = () => {
    const saved = savedCropZonesRef.current;
    if (saved.length !== cropZonesChecked.length) return true;
    const savedSet = new Set(saved);
    return cropZonesChecked.some(id => !savedSet.has(id));
  };

  const handleClose = () => {
    if (hasUnsavedChanges()) {
      const {
        title,
        content,
        okText,
        cancelText
      } = LayoutStrings.modals.discardPropertySelection;
      showConfirmModal({
        title,
        content: <span className="text-sm">{content}</span>,
        okText,
        cancelText,
        icon: <ReportProblemIcon />,
        okButtonProps: {
          style: {
            borderRadius: 4,
            background: '#14803C',
            padding: '10px 16px',
            color: '#FFF',
            fontSize: 14,
            fontWeight: 400,
            marginLeft: 8
          }
        },
        onOk: handleDiscardPropertySelection,
        width: 423
      });
    } else {
      handleClearSearch();
      onClose();
    }
  };

  const getCropzoneFeatures = (fieldId: string) =>
    fieldsAndAreasGeoJSONCollection.features?.filter(
      (feature: any) => fieldId === feature.properties.id
    ) || [];

  const fieldContainsCropzone = (field: any, cropzoneId: string) =>
    field.cropzones.some((cz: any) => cz.id === cropzoneId);

  const propertyContainsCropzone = (property: any, cropzoneId: string) =>
    property.fields.some((field: any) =>
      fieldContainsCropzone(field, cropzoneId)
    );

  const emptyFieldInfo = {
    fieldId: null,
    fieldName: '',
    isFieldArchived: false,
    isPropertyArchived: false
  };

  const findFieldInfoForCropzone = (cropzoneId: string) => {
    const property = propertyLandingPageData.properties.find((prop: any) =>
      propertyContainsCropzone(prop, cropzoneId)
    );
    if (!property) return emptyFieldInfo;
    const field = property.fields.find((_field: any) =>
      fieldContainsCropzone(_field, cropzoneId)
    );
    return field
      ? {
          fieldId: field.id,
          fieldName: field.name,
          isFieldArchived: !!field.archived,
          isPropertyArchived: !!property.archived
        }
      : emptyFieldInfo;
  };

  const enhanceCropzoneWithFieldData = (cropzone: any) => {
    const {
      fieldId,
      fieldName,
      isFieldArchived,
      isPropertyArchived
    } = findFieldInfoForCropzone(cropzone.id);
    return {
      ...cropzone,
      area: cropzone.reportedArea || cropzone.boundaryArea,
      fieldName,
      fieldId,
      isFieldArchived,
      isPropertyArchived,
      fieldGeometry: fieldId ? getCropzoneFeatures(fieldId) : []
    };
  };

  const onSelectionApply = () => {
    const baseCropZones = getSelectedCropZones(
      propertyLandingPageData.properties,
      cropZonesChecked
    );
    const existingById: Record<string, any> = {};
    selectionAdapter.existingCropZones.forEach(zone => {
      if (zone?.id) existingById[zone.id] = zone;
    });
    const mappedCropZones = baseCropZones.map(cropzone => {
      const enhanced = enhanceCropzoneWithFieldData(cropzone);
      const existing = existingById[cropzone.id];
      if (existing) {
        return {
          ...enhanced,
          area: existing.area ?? enhanced.area,
          appliedAcreage: existing.appliedAcreage,
          coveragePercent: existing.coveragePercent,
          selectedAreaType: existing.selectedAreaType
        };
      }
      return enhanced;
    });
    selectionAdapter.onConfirm({
      cropZoneIds: cropZonesChecked,
      totalArea,
      cropZones: mappedCropZones,
      sourceType: 'CROP_ZONE'
    });
    savedCropZonesRef.current = [...cropZonesChecked];
    handleClearSearch();
    onClose();
  };

  const isReloadingProperties =
    loading || fieldsLoading || isGlobalCropSeasonLoading;

  return (
    <Drawer
      width="80%"
      open={open}
      onClose={handleClose}
      bodyStyle={{ overflow: 'hidden', padding: 0, height: '100%' }}
      closable={false}
      className="select-properties-drawer-v2"
    >
      <div
        data-testid="select-properties-drawer-v2"
        className="h-full flex flex-row"
        style={{ paddingBottom: 80 }}
      >
        {/* Left panel */}
        <section
          className="flex flex-col tree-left-panel"
          style={{
            width: '41%',
            flexShrink: 0,
            minWidth: 0,
            padding: '24px 16px 0 24px'
          }}
        >
          {/* Header: title + crop season + search row */}
          <div
            className="flex flex-col shrink-0"
            style={{ gap: 12, marginBottom: 4 }}
          >
            <h1 className="text-2xl font-semibold m-0 leading-tight">
              Select Properties
            </h1>

            {activeCropSeasonNames && (
              <div className="flex flex-col">
                <span
                  className="text-xs leading-5"
                  style={{ color: '#696F88' }}
                >
                  Crop Season
                </span>
                <CropSeasonDisplay cropSeasonNames={activeCropSeasonNames} />
              </div>
            )}

            {restrictToCropId && (
              <div className="flex flex-col" style={{ marginTop: -20 }}>
                <span
                  className="text-xs leading-5"
                  style={{ color: '#696F88' }}
                >
                  Crop
                </span>
                <div
                  className="flex items-center gap-x-1 leading-none"
                  style={{ fontSize: '14px' }}
                >
                  <span className="inline-flex items-center">
                    <CropIconMapping cropObject={crops as any} size={20} />
                  </span>
                  <span className="leading-none">{(crops as any)?.name}</span>
                  <Tooltip
                    title={
                      <>
                        Only properties with the
                        <br />
                        selected crop are listed
                      </>
                    }
                    placement="bottomLeft"
                    color="#232630"
                    align={{ offset: [-13, 0] }}
                  >
                    <span
                      className="inline-flex self-start rounded-full cursor-pointer spv2-info-icon-hover"
                      style={{ padding: '2px', margin: '-2px' }}
                    >
                      <img
                        src={InfoIcon}
                        alt="Only properties with the selected crop are listed"
                        width={18}
                        height={18}
                        className="block"
                      />
                    </span>
                  </Tooltip>
                </div>
              </div>
            )}

            <div className="flex items-start gap-2">
              <div className="flex-1" ref={searchWrapperRef}>
                <SearchInputLandingProperties
                  drawer
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const valueInput = event.target.value.toLowerCase();
                    dispatch({
                      type: SEARCH_TEXT_VALUE_FFT,
                      payload: { valueInput }
                    });
                  }}
                  value={searchTextValueFFT?.valueInput}
                  placeholder="Search properties"
                />
              </div>
              <Dropdown
                menu={sortMenuProps}
                trigger={['click']}
                placement="bottomRight"
                overlayClassName="group-by-dropdown-v2"
              >
                <Tooltip title="Group by" placement="top" color="#232630">
                  <button
                    type="button"
                    aria-label="Group by"
                    data-testid="group-by-sort-button"
                    className="w-8 h-8 flex items-center justify-center rounded bg-white border border-neutral-30 hover:bg-neutral-100 shrink-0"
                  >
                    <img src={SortIcon} alt="" aria-hidden="true" />
                  </button>
                </Tooltip>
              </Dropdown>
            </div>
          </div>

          {/* FFT */}
          {isReloadingProperties ? (
            <div className="w-full pt-54px flex flex-col items-center justify-start">
              <img
                src={SpinnerIcon}
                alt=""
                aria-hidden="true"
                className="animate-spin"
                data-testid="gr-spinner"
              />
              <p className="text-base mt-4">Loading farms...</p>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto overflow-x-hidden">
              <PropertySelectorV2
                properties={groupedProperties}
                cropZonesChecked={cropZonesChecked}
                setCropZonesChecked={setCropZonesChecked}
                groupBy={groupBy}
                onSelectAll={handleSelectAll}
                geoJSONCollection={fieldsAndAreasGeoJSONCollection}
                searchTerm={activeSearchTerm}
                scrollToField={scrollToField}
                searchCountMap={searchCountMap}
                fieldCzCountMap={fieldCzCountMap}
              />
            </div>
          )}
        </section>

        {/* Right panel: close button + map */}
        <section
          className="map-right-panel flex flex-col flex-1"
          style={{ minWidth: 0, position: 'relative' }}
        >
          {/* Close button row */}
          <div
            className="flex justify-end shrink-0"
            style={{ padding: '24px 24px 0 0' }}
          >
            <button
              type="button"
              onClick={handleClose}
              className="w-8 h-8 hover:bg-gray-100 rounded cursor-pointer flex items-center justify-center"
              aria-label="Close drawer"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
              >
                <path
                  d="M14.25 4.8075L13.1925 3.75L9 7.9425L4.8075 3.75L3.75 4.8075L7.9425 9L3.75 13.1925L4.8075 14.25L9 10.0575L13.1925 14.25L14.25 13.1925L10.0575 9L14.25 4.8075Z"
                  fill="#696F88"
                />
              </svg>
            </button>
          </div>

          {/* Map */}
          <div
            className="flex-1 map-inner"
            style={{ padding: '8px 24px 16px 0' }}
          >
            <SelectionMap
              geoJSONCollection={mapGeoJSON}
              cropZonesChecked={cropZonesChecked}
              center={mapCoordinates}
              zoom={zoom}
              fitBoundsTarget={fitBoundsTarget}
              onZoomChange={setZoom}
              onCenterChange={setManualCoords}
              onClickFeature={
                isReloadingProperties ? undefined : onSelectFromMap
              }
              searchActive={!!activeSearchTerm}
              visibleFieldIds={visibleFieldIds}
              onClearSearch={handleClearSearch}
              isSearchLoading={isSearchLoading || isReloadingProperties}
            />
          </div>
        </section>
      </div>

      {/* Footer */}
      <footer
        className="absolute bottom-0 w-full left-0 right-0 flex items-center justify-between border-t border-neutral-20 px-6 pt-4 pb-6"
        style={{ zIndex: 10 }}
      >
        <div className="flex items-center gap-4">
          <span className="text-sm leading-5" style={{ color: '#0071CD' }}>
            {cropZonesChecked.length} selected crop zone
            {cropZonesChecked.length === 1 ? '' : 's'}
          </span>
          <div className="flex items-center gap-1" style={{ color: '#0071CD' }}>
            <FieldZoneIcon color="#0092E4" />
            <span className="text-sm leading-5">
              {formatNumber(totalArea)} total ac
            </span>
          </div>
        </div>
        <div className="flex gap-4">
          <Button
            type={ButtonType.outline}
            size="large"
            style={{
              width: 76,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            type={ButtonType.primary}
            size="large"
            style={{
              width: 63,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onClick={onSelectionApply}
            disabled={
              !cropZonesChecked?.length ||
              isReloadingProperties ||
              !hasUnsavedChanges()
            }
          >
            Save
          </Button>
        </div>
      </footer>

      {showSelectAllOverlay && (
        <>
          <div className="select-all-loading-backdrop" />
          <div
            className="select-all-loading-container"
            data-testid="select-all-loading-overlay"
            role="status"
            aria-live="polite"
          >
            <div className="select-all-loading-panel">
              <img
                src={SpinnerIcon}
                alt="spinner"
                width="72"
                height="72"
                className="animate-spin"
                style={{ animationDuration: '2s' }}
                aria-hidden="true"
              />
              <span className="select-all-loading-text">
                Selecting properties...
              </span>
            </div>
          </div>
        </>
      )}
    </Drawer>
  );
};

export default SelectPropertiesDrawerV2;
